import { SERVER_URL } from '../config.js';
import { storage, clearPersistedClientData } from './storage.js';
import { fetchCache } from './fetchCache.js';
import { createRetryableHttpError, isTransientHttpStatus, shouldRetryMethod, withRetry } from './error-handling.js';

const isAuthFailureStatus = (status) => {
  const code = Number(status);
  if (!Number.isFinite(code) || code < 400 || code >= 500) return false;
  return !isTransientHttpStatus(code);
};

export class TokenManager {
  constructor() {
    this.refreshPromise = null;
    this.isRefreshing = false;
    this.refreshCallbacks = [];
    this.tokenCheckInterval = null;
    this.TOKEN_CHECK_INTERVAL = 5 * 60000;
    this.REFRESH_BUFFER_TIME = 5 * 60000;

    this.startTokenChecks();
  }

  setTokens(accessToken, refreshToken = null, userData = null) {
    try {
      storage.set('auth_token', accessToken);
    } catch {}

    if (userData) {
      try { storage.set('userData', userData); } catch {}
    }

    const tokenExp = this.getTokenExpiration(accessToken);
    if (tokenExp) {
      try { storage.set('token_expiry', tokenExp.toString()); } catch {}
    }
  }

  getAccessToken() {
    try {
      const v = storage.get('auth_token');
      return typeof v === 'string' ? v : null;
    } catch {
      return null;
    }
  }

  getRefreshToken() {
    // HttpOnly cookie, not accessible via JS
    return null;
  }

  getTokenExpiration(token) {
    try {
      return JSON.parse(atob(token.split('.')[1])).exp * 1000;
    } catch {
      return null;
    }
  }

  hasRefreshableSession() {
    if (this.getAccessToken()) return true;
    try {
      return storage.get('userData')?.isLoggedIn === true;
    } catch {
      return false;
    }
  }

  needsRefresh() {
    const token = this.getAccessToken();
    if (!token) return false;

    const expiry = this.getTokenExpiration(token);
    return expiry ? (expiry - Date.now()) <= this.REFRESH_BUFFER_TIME : false;
  }

  _resolveRefreshWaiters(token) {
    this.refreshCallbacks.forEach(callback => callback(token));
    this.refreshCallbacks = [];
  }

  _unexpiredAccessToken() {
    const token = this.getAccessToken();
    if (!token) return null;
    const expiry = this.getTokenExpiration(token);
    if (expiry && expiry <= Date.now()) return null;
    return token;
  }

  async teardownAuthSession() {
    const tokenForLogout = this.getAccessToken();
    await this.clearTokens();

    try {
      const headers = { 'Content-Type': 'application/json' };
      if (tokenForLogout) headers.Authorization = `Bearer ${tokenForLogout}`;
      await fetchCache.fetch(`${SERVER_URL}/api/v1/auth/logout`, {
        method: 'POST',
        headers,
        credentials: 'include',
        skipCache: true
      });
    } catch {
      // Cookie clear is best-effort; local session is already torn down.
    }

    this.dispatchAuthFailure();
  }

  async refreshAccessToken() {
    if (this.isRefreshing) {
      return new Promise(resolve => this.refreshCallbacks.push(resolve));
    }

    if (!this.hasRefreshableSession()) {
      return null;
    }

    this.isRefreshing = true;

    try {
      const data = await fetchCache.fetch(`${SERVER_URL}/api/v1/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        skipCache: true,
        allowUnsafeRetry: true
      });

      const newAccessToken = data?.access_token;
      if (!newAccessToken) throw new Error('Token refresh failed');

      storage.set('auth_token', newAccessToken);

      const tokenExp = this.getTokenExpiration(newAccessToken);
      if (tokenExp) storage.set('token_expiry', tokenExp.toString());

      this._resolveRefreshWaiters(newAccessToken);
      return newAccessToken;
    } catch (error) {
      if (isAuthFailureStatus(error?.status)) {
        await this.teardownAuthSession();
        this._resolveRefreshWaiters(null);
        return null;
      }

      const existing = this._unexpiredAccessToken();
      this._resolveRefreshWaiters(existing);
      return existing;
    } finally {
      this.isRefreshing = false;
    }
  }

  async getValidAccessToken() {
    let token = this.getAccessToken();
    if (!token) {
      if (!this.hasRefreshableSession()) {
        return null;
      }
      const refreshed = await this.refreshAccessToken();
      if (!refreshed) {
        this.dispatchAuthRequired('no_access_token');
        return null;
      }
      return refreshed;
    }

    if (this.needsRefresh()) {
      const refreshed = await this.refreshAccessToken();
      if (refreshed) return refreshed;
      return this._unexpiredAccessToken();
    }

    return token;
  }

  static clearUserData() {
    storage.remove('userData');
  }

  async clearTokens() {
    await clearPersistedClientData();
  }

  startTokenChecks() {
    if (this.tokenCheckInterval) clearInterval(this.tokenCheckInterval);

    this.tokenCheckInterval = setInterval(() => {
      if (this.needsRefresh()) {
        this.refreshAccessToken().catch(() => { });
      }
    }, this.TOKEN_CHECK_INTERVAL);
  }

  stopTokenChecks() {
    if (this.tokenCheckInterval) {
      clearInterval(this.tokenCheckInterval);
      this.tokenCheckInterval = null;
    }
  }

  dispatchAuthRequired(reason) {
    document.dispatchEvent(new CustomEvent('authRequired', {
      detail: { reason, message: `${reason.replace(/_/g, ' ')} - authentication required` }
    }));
  }

  dispatchAuthFailure() {
    document.dispatchEvent(new CustomEvent('auth:token:expired', {
      detail: { reason: 'Token refresh failed' }
    }));

    document.dispatchEvent(new CustomEvent('auth:logout', {
      detail: { reason: 'Token refresh failed' }
    }));
  }

  getUserId() {
    const userData = storage.get('userData');
    if (!userData) return null;

    try {
      return userData.id || userData.user_id || null;
    } catch {
      return null;
    }
  }

  isAuthenticated() {
    const token = this.getAccessToken();
    if (!token) return false;

    const expiry = this.getTokenExpiration(token);
    return expiry ? expiry > Date.now() : true;
  }

  /**
   * @param {string | URL} url
   * @param {(RequestInit & {
   *   _isRetry?: boolean;
   *   _allowUnsafeRetry?: boolean;
   *   _retryTelemetry?: (payload: any) => void;
   * })} [options={}]
   * @returns {Promise<Response>}
   */
  async authenticatedFetch(url, options = {}) {
    const token = await this.getValidAccessToken();
    if (!token) throw new Error('No valid access token');

    const method = String(options.method || 'GET').toUpperCase();
    const {
      _isRetry = false,
      _allowUnsafeRetry = false,
      _retryTelemetry = null,
      ...fetchOptions
    } = options;

    const incomingHeaders = /** @type {Record<string, string>} */ (fetchOptions.headers || {});
    const headers = {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
      ...incomingHeaders
    };

    const retryConfig = {
      method,
      endpoint: String(url),
      allowUnsafeMethods: _allowUnsafeRetry,
      onRetryEvent: (eventPayload) => {
        if (typeof _retryTelemetry === 'function') _retryTelemetry(eventPayload);
      }
    };

    return withRetry(async () => {
      const response = await fetch(url, {
        ...fetchOptions,
        headers,
        credentials: 'include' // sends HttpOnly cookies with requests
      });

      if (response.status === 401 && !_isRetry) {
        const newToken = await this.refreshAccessToken();
        if (newToken) {
          return this.authenticatedFetch(url, {
            ...fetchOptions,
            _isRetry: true,
            _allowUnsafeRetry,
            _retryTelemetry,
            headers: { ...incomingHeaders, 'Authorization': `Bearer ${newToken}` }
          });
        }
      }

      if (isTransientHttpStatus(response.status) && shouldRetryMethod(method, _allowUnsafeRetry)) {
        throw createRetryableHttpError(response, { endpoint: url, method });
      }

      return response;
    }, retryConfig, { label: 'authenticatedFetch', endpoint: String(url), method });
  }

  initialize() {
    const token = this.getAccessToken();
    if (token && this.needsRefresh()) {
      this.refreshAccessToken().catch(() => { });
    }
  }
}

export const tokenManager = new TokenManager();

if (typeof window !== 'undefined') {
  window.tokenManager = tokenManager;
}

tokenManager.initialize();
